import type { CadInstance, DetectSnapshot } from "../../../bimgo";
import { CATALOG, decideUpdate, openHostYears, pluginView, updateTier, type PluginId, type VersionState } from "../../../lib/plugins";

/** The write a year is waiting for; worded per kind. */
export type ManageAction = "install" | "update" | "downgrade" | "repair";

export interface ManageItem {
  /** `revit:2025`: stable across refreshes, so a row keeps its place, its tick and its result. */
  key: string;
  vendor: PluginId;
  /** "Revit", "AutoCAD": the product name the user knows. */
  host: string;
  year: string;
  state: VersionState;
  /** The build on disk, when one is recorded. */
  installed: string | null;
  /** The pending write, or null when there is none (current, developer build, no build). */
  action: ManageAction | null;
  /** The version on disk an update or roll back moves from. */
  from: string | null;
  /** The version the write moves to. */
  to: string | null;
  /** The write is a command-set patch, applied to a running host by design. */
  live: boolean;
  /** Something of ours is on disk for this year. */
  removable: boolean;
  /** Its CAD host is open: a whole-tree write or removal asks for it to be closed first. */
  hostOpen: boolean;
  /** This plugin version publishes no build for this year and nothing is installed. */
  unsupported: boolean;
  restartRequired: boolean;
}

/** The products in scope: one card's product, or every product when none is named. */
const entriesFor = (vendor?: PluginId) => CATALOG.filter((entry) => !vendor || entry.id === vendor);

/**
 * Every detected product year, Revit then AutoCAD, newest year first; only `vendor`'s years
 * when one is given. Built on the Plugins pane's own per-year view (`pluginView`), so a row
 * never decides differently from the card.
 *
 * A developer build (`external`) can be removed but is never written over: it is someone's
 * deliberate local plugin.
 */
export function manageItems(
  snapshots: Partial<Record<PluginId, DetectSnapshot>> | null,
  instances: readonly CadInstance[],
  vendor?: PluginId,
  canPatch = false,
): ManageItem[] {
  const items: ManageItem[] = [];
  for (const entry of entriesFor(vendor)) {
    const snapshot = snapshots?.[entry.id];
    if (!snapshot || !entry.available) continue;
    const view = pluginView(snapshots, instances, entry.id);
    for (const version of view?.versions ?? []) {
      const installed = version.installedPluginVersion ?? null;
      const from = version.state === "ready" ? installed : null;
      const to = version.unsupported || version.state === "external" ? null : version.updateTo;
      const action: ManageAction | null = !to
        ? null
        : version.state === "incomplete" || version.state === "orphaned"
          ? "repair"
          : version.state === "absent"
            ? "install"
            : from && updateTier(from, to) === "downgrade" ? "downgrade" : "update";
      items.push({
        key: `${entry.id}:${version.version}`,
        vendor: entry.id,
        host: entry.hostName,
        year: version.version,
        state: version.state,
        installed,
        action,
        from: action ? from : null,
        to,
        live: canPatch && action === "update" && decideUpdate(from, to!) === "patch",
        removable: version.state !== "absent",
        hostOpen: version.running > 0 || openHostYears(snapshot, [version.version]).length > 0,
        unsupported: version.unsupported,
        restartRequired: Boolean(version.restartRequired),
      });
    }
  }
  return items;
}

/**
 * Whether a whole-tree write or a removal of this year has to wait for its CAD host to close.
 * Such a year stays tickable: the run asks the user to close it first (#189), as the per-year
 * Manage buttons always did, rather than leaving them to find the refresh button.
 */
export const needsClosedHost = (item: ManageItem, op: "write" | "remove") =>
  item.hostOpen && (op === "remove" || !item.live);
export const canWrite = (item: ManageItem) => item.action !== null;
export const canRemove = (item: ManageItem) => item.removable;
export const selectable = (item: ManageItem) => canWrite(item) || canRemove(item);

/** What starts ticked: every row something can be done to. */
export function defaultSelection(items: readonly ManageItem[]): string[] {
  return items.filter(selectable).map((item) => item.key);
}

/** Rows with a write waiting, whether or not it can run now: the card's badge. */
export function pendingCount(items: readonly ManageItem[]): number {
  return items.filter((item) => item.action !== null).length;
}

export type ManageEmptyState = "unavailable" | "no-hosts" | "no-candidates" | "current";

/** Explain why nothing can be installed without claiming that an unreachable build is current. */
export function manageEmptyState(
  snapshots: Partial<Record<PluginId, DetectSnapshot>> | null,
  vendor?: PluginId,
): ManageEmptyState {
  let detected = 0;
  let current = 0;
  let checked = false;
  let unavailable = false;
  for (const entry of entriesFor(vendor)) {
    const snapshot = snapshots?.[entry.id];
    if (!snapshot || !entry.available) continue;
    checked = true;
    const view = pluginView(snapshots, [], entry.id);
    detected += view?.unsupportedVersions.length ?? 0;
    for (const version of view?.versions ?? []) {
      detected++;
      // A deliberately external build is outside this installer's responsibility.
      if (version.state === "external") continue;
      const plan = snapshot.plans?.[version.version];
      if (!plan || (plan.action === "unavailable" && plan.cause === "no-version")) {
        unavailable = true;
      } else if (version.state === "ready" && plan.action === "current" &&
        plan.version === version.installedPluginVersion) {
        current++;
      }
    }
  }
  if (!checked || unavailable) return "unavailable";
  if (detected === 0) return "no-hosts";
  return current === detected ? "current" : "no-candidates";
}
